import * as giraffe from '@influxdata/giraffe';
// import { Table } from '@influxdata/giraffe';
import { AnnotationMark } from '@influxdata/giraffe/dist/types';
import moment from 'moment';
import { useMemo } from 'react';

export const ColorSchemeOptions = {
  NINETEEN_EIGHTY_FOUR: giraffe.NINETEEN_EIGHTY_FOUR,
  ATLANTIS: giraffe.ATLANTIS,
  DO_ANDROIDS_DREAM: giraffe.DO_ANDROIDS_DREAM,
  DELOREAN: giraffe.DELOREAN,
  CTHULHU: giraffe.CTHULHU,
  ECTOPLASM: giraffe.ECTOPLASM,
  PRIMARY: giraffe.PRIMARY,
  PRIMARY_REVERSE: giraffe.PRIMARY_REVERSE,
  T_MAX_400_FILM: giraffe.T_MAX_400_FILM,
  RAINBOW_EIGHT: giraffe.RAINBOW_EIGHT,
  RAINBOW_SIXTEEN: giraffe.RAINBOW_SIXTEEN,
  VIRIDIS: giraffe.VIRIDIS,
  MAGMA: giraffe.MAGMA,
  INFERNO: giraffe.INFERNO,
  PLASMA: giraffe.PLASMA,
  YL_OR_RD: giraffe.YL_OR_RD,
  YL_GN_BU: giraffe.YL_GN_BU,
  BU_GN: giraffe.BU_GN,
  SOLID_BLUE: giraffe.SOLID_BLUE,
  SOLID_GREEN: giraffe.SOLID_GREEN,
  SOLID_RED: giraffe.SOLID_RED,
  SOLID_YELLOW: giraffe.SOLID_YELLOW,
  SOLID_PURPLE: giraffe.SOLID_PURPLE,
};

interface SampleAnnotationsCreatorOptions {
  table: giraffe.Table;
}

const DEFAULT_COLOR = 'green';
const decimalPlaces = 2;
const lineCount = 6;
const smallLineCount = 10;
const valuePositionYOffset = 0.5;
const valuePositionXOffset = 0;
const gaugeSize = Number(Math.PI.toFixed(2));

const minLineWidth = 22;
const gaugeMin = 0;
const gaugeMax = 100;
const prefix = '';
const suffix = '';
const tickPrefix = '';
const tickSuffix = '';

export const findStringColumns = (table: giraffe.Table) =>
  table.columnKeys.filter(k => {
    // Only include whitelisted string columns
    const allowedCols = ['_measurement', '_field', 'city', 'rs_serial', 'deviceState'];

    if (!allowedCols.includes(k)) return false;
    if (table.getColumnType(k) !== 'string') return false;

    const values = table.getColumn(k) as any[];
    return values?.some(val => typeof val === 'string' && val.trim() !== '');
  });

export function isDataRenderable(table, type) {
  if (!table) return false;

  const colNames = table?.columnKeys || [];
  if (!Array.isArray(colNames) || colNames.length === 0) return false;

  // Map type doesn’t require _value
  if (!['map'].includes(type) && !colNames.includes('_value')) return false;

  let values;
  if (!['map', 'table'].includes(type)) {
    try {
      values = table.getColumn('_value');
    } catch (err) {
      return false; // table structure invalid
    }

    if (!Array.isArray(values) || values.length === 0) return false;

    // Remove null/undefined
    const nonEmptyValues = values.filter(v => v !== null && v !== undefined);

    if (nonEmptyValues.length === 0) return false;

    // If all values are strings → not renderable
    const allStrings = nonEmptyValues.every(v => typeof v === 'string');

    if (allStrings) return false;

    // If all values are NaN after number conversion → not renderable
    const allNaN = nonEmptyValues.every(v => isNaN(Number(v)));

    if (allNaN) return false;
  }

  return true;
}

export const matchAnnotationsToTable = (options: SampleAnnotationsCreatorOptions): AnnotationMark[] => {
  const { table } = options;
  const instances = table.getColumn('_time', 'number');
  const value = table.getColumn('_value', 'number');
  const instanceValues = [...new Set(instances)];
  const annotationMarks: AnnotationMark[] = [];

  instanceValues.forEach((instance: number, i: number) => {
    if (value?.[i] !== 0) {
      annotationMarks.push({
        title: `Anomaly`,
        description: `${value?.[i]} anomaly at ${moment(instance).format('YYYY-MM-DD HH:mm:ss')}`,
        color: 'tomato',
        dimension: 'x',
        startValue: parseFloat(instance.toPrecision(2)),
        stopValue: parseFloat(instance.toPrecision(2)),
        pin: 'none',
      });
    }
  });

  return annotationMarks;
};

export const getAnomalyLayer = (anomalyTable: giraffe.Table): giraffe.AnnotationLayerConfig => {
  return {
    type: 'annotation',
    annotations: matchAnnotationsToTable({ table: anomalyTable }),
    x: '_time',
    y: '_value',
    fill: [],
  };
};

const getMainLayer = ({
  graphType,
  colorScheme,
  table,
  isAnomalyChart,
}: {
  graphType: string;
  colorScheme: string | null;
  table?: giraffe.Table;
  isAnomalyChart?: boolean;
}): giraffe.LayerConfig => {
  switch (graphType) {
    case 'histogram':
      return {
        type: 'histogram',
        colors: ColorSchemeOptions[colorScheme] || giraffe.ATLANTIS,
        x: '_value',
        fill: ['_field', '_measurement', 'deviceState'],
        position: 'stacked',
        binCount: 5,
        fillOpacity: 1,
        strokePadding: 0.1,
        strokeOpacity: 0.8,
      };
    case 'table':
      return {
        type: 'simple table',
        showAll: true,
      };
    case 'scatter':
      return {
        type: 'scatter',
        x: '_time',
        y: '_value',
        fill: ['_field'],
        colors: ['#468352', '#c885b5', '#ee7015'],
      };
    case 'line':
      return {
        type: 'line',
        position: undefined,
        colors: ColorSchemeOptions[colorScheme] || giraffe.CTHULHU,
        x: '_time',
        y: '_value',
        shadeBelow: true,
        fill: table ? findStringColumns(table) : [],
      };
    case 'single stat':
      return {
        type: 'single stat',
        prefix,
        suffix,
        decimalPlaces: {
          isEnforced: true,
          digits: decimalPlaces,
        },
        textColor: ColorSchemeOptions[colorScheme]?.[0] || giraffe.CTHULHU,
        textOpacity: 1,
      };
    case 'gauge':
      return {
        type: 'gauge',
        prefix,
        suffix,
        tickPrefix,
        tickSuffix,
        decimalPlaces: {
          isEnforced: true,
          digits: decimalPlaces,
        },
        gaugeColors: [
          {
            ...giraffe.DEFAULT_GAUGE_COLORS[0],
            hex: ColorSchemeOptions[colorScheme]?.[0] || giraffe.DEFAULT_GAUGE_COLORS[0]?.hex,
            value: gaugeMin,
          },
          {
            ...giraffe.DEFAULT_GAUGE_COLORS[1],
            hex: ColorSchemeOptions[colorScheme]?.[1] || giraffe.DEFAULT_GAUGE_COLORS[1]?.hex,
            value: gaugeMax,
          },
        ],
        gaugeSize,
        gaugeTheme: {
          valuePositionYOffset,
          valuePositionXOffset,
          lineCount,
          lineColor: ColorSchemeOptions[colorScheme]?.[2] || giraffe.CTHULHU,
          smallLineCount,
          minLineWidth,
        } as giraffe.GaugeTheme,
      };
    case 'map':
      return {
        type: 'geo',
        lat: null, // auto-center
        lon: null, // auto-center
        zoom: 3,
        allowPanAndZoom: true,
        detectCoordinateFields: false,
        useS2CellID: false,
        latLonColumns: {
          lat: { key: 'rs_lat', column: 'rs_lat' },
          lon: { key: 'rs_long', column: 'rs_long' },
        },
        layers: [
          {
            type: 'pointMap',
            colorDimension: { label: 'Duration' },
            colorField: 'duration',
            colors: [
              { type: 'min', hex: ColorSchemeOptions[colorScheme]?.[0] || '#ff0000' },
              { value: 50, hex: ColorSchemeOptions[colorScheme]?.[1] || '#343aeb' },
              { type: 'max', hex: ColorSchemeOptions[colorScheme]?.[2] || '#343aeb' },
            ],
            isClustered: false,
            tooltipColumns: ['_field', '_measurement', '_time'],
          },
        ],
        tileServerConfiguration: {
          tileServerUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        },
      } as giraffe.GeoLayerConfig;

    default:
      return {
        type: 'histogram',
        colors: ColorSchemeOptions[colorScheme] || giraffe.CTHULHU,
        x: '_time',
        fill: ['_field', '_measurement'],
        position: 'stacked',
        binCount: 30,
        fillOpacity: 1,
      };
  }
};

export const getLayerConfigs = ({
  graphType,
  colorScheme,
  anomalyTable,
  table,
  isAnomalyChart,
}: {
  graphType: string;
  colorScheme: string | null;
  anomalyTable?: giraffe.Table;
  table?: giraffe.Table;
  isAnomalyChart?: boolean;
}): Array<giraffe.LayerConfig> => {
  const anomalyCustomLayer = useMemo(() => {
    if (isAnomalyChart && !!anomalyTable) return anomalyTable ? getAnomalyLayer(anomalyTable) : null;
  }, [anomalyTable, isAnomalyChart]);

  const mainLayer = getMainLayer({ graphType, colorScheme, table, isAnomalyChart });
  if (anomalyCustomLayer) {
    return [mainLayer, anomalyCustomLayer];
  }
  return [mainLayer];
};